import React, { useState } from 'react';
import { ILeaseMaster } from 'src/models/dlcLease/ILeaseMaster';
import * as styles from './styles';

interface INotesSectionProps {
    leaseMaster: ILeaseMaster;
    onChange?: (field: string, value: unknown) => void;
    onSave?: () => Promise<boolean>;
}

const NotesSection: React.FC<INotesSectionProps> = ({ leaseMaster, onChange, onSave }) => {
    const isPotentiallyEditable = !!onChange;
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const handleChange = (value: string) => {
        if (onChange) {
            onChange('notes', value);
        }
    };

    const notesContent = leaseMaster.notes || '';

    const handleToggleEdit = async (): Promise<void> => {
        if (!isEditing) {
            setIsEditing(true);
            return;
        }

        setIsSaving(true);
        const wasSaved = (await onSave?.()) ?? true;
        setIsSaving(false);
        if (wasSaved) {
            setIsEditing(false);
        }
    };

    return (
        <div style={styles.sectionContainer}>
            <h2 style={styles.sectionHeader}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={styles.colors.primary} strokeWidth="2">
                        <path d="M12 20h9" />
                        <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                    </svg>
                    Notes
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    {notesContent && (
                        <span
                            style={{
                                ...styles.badge,
                                backgroundColor: styles.colors.primaryLight + '20',
                                color: styles.colors.primary,
                                fontSize: styles.typography.fontSize.xs,
                            }}
                        >
                            {notesContent.split(' ').length} words
                        </span>
                    )}
                    {isPotentiallyEditable && (
                        <button
                            onClick={handleToggleEdit}
                            disabled={isSaving}
                            style={isEditing ? styles.buttonSuccess : styles.buttonSecondary}
                            onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
                            onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                        >
                            {isEditing ? (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    <polyline points="20 6 9 17 4 12" />
                                </svg>
                            ) : (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                </svg>
                            )}
                            <span style={{ marginLeft: '0.5rem' }}>{isSaving ? 'Saving...' : isEditing ? 'Done' : 'Edit'}</span>
                        </button>
                    )}
                </div>
            </h2>

            {!notesContent && !isPotentiallyEditable ? (
                <div style={styles.emptyState}>
                    <svg
                        width="48"
                        height="48"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke={styles.colors.gray400}
                        strokeWidth="1.5"
                        style={{ margin: '0 auto 1rem' }}
                    >
                        <path d="M12 20h9" />
                        <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                        <line x1="16" y1="8" x2="2" y2="8" />
                        <line x1="16" y1="12" x2="2" y2="12" />
                        <line x1="10" y1="16" x2="2" y2="16" />
                    </svg>
                    <p style={{ margin: 0, color: styles.colors.gray600 }}>No notes available</p>
                    {isPotentiallyEditable && (
                        <p style={{ marginTop: '0.5rem', fontSize: styles.typography.fontSize.sm, color: styles.colors.gray500 }}>
                            Click above to start adding notes
                        </p>
                    )}
                </div>
            ) : (
                <div style={{ position: 'relative' }}>
                    <textarea
                        value={notesContent}
                        disabled={!isEditing}
                        onChange={(e) => handleChange(e.target.value)}
                        placeholder={isEditing ? 'Enter your notes here...' : 'Click "Edit" to make changes'}
                        autoComplete="off"
                        onFocus={(e) => {
                            if (isEditing) {
                                e.currentTarget.style.borderColor = styles.colors.borderFocus;
                                e.currentTarget.style.boxShadow = styles.inputFocus.boxShadow;
                            }
                        }}
                        onBlur={(e) => {
                            e.currentTarget.style.borderColor = styles.colors.gray300;
                            e.currentTarget.style.boxShadow = 'none';
                        }}
                        style={{
                            ...(!isEditing ? { ...styles.textareaBase, ...styles.inputDisabled } : styles.textareaBase),
                            minHeight: '200px',
                            lineHeight: '1.75',
                            fontFamily: styles.typography.fontFamily,
                        }}
                    />
                    {isEditing && (
                        <div
                            style={{
                                position: 'absolute',
                                bottom: '-1.5rem',
                                left: 0,
                                fontSize: styles.typography.fontSize.sm,
                                color: styles.colors.gray500,
                            }}
                        >
                            <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                style={{ display: 'inline', marginRight: '0.25rem' }}
                            >
                                <circle cx="11" cy="11" r="8" />
                                <path d="m21 21-4.35-4.35" />
                            </svg>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default NotesSection;



// WEBPACK FOOTER //
// ./src/components/leases/sections/Notes.tsx